import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import { sectionsAPI } from "../services/sectionsAPI";
import { formatAPIError, validateEmail, validateName } from "../utils/validation";
import "./styles/CandidateReferences.css";

const TABS = ["Requests", "Team Structure"];

const CandidateReferences = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("Requests");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // NEW: Support multiple employments
  const [employments, setEmployments] = useState([]);
  const [selectedEmploymentId, setSelectedEmploymentId] = useState("");

  // Requests
  const [references, setReferences] = useState([]);
  const [refSearch, setRefSearch] = useState("");

  // Send Reference Modal
  const [sendOpen, setSendOpen] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendForm, setSendForm] = useState({
    name: "",
    email: "",
    designation: "",
    contactNumber: "",
    teamId: "",
    questionnaireTemplateId: "",
  });
  const [sendErrors, setSendErrors] = useState({});

  // Team Structure
  const [teams, setTeams] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);

  // Create Team
  const [createTeamOpen, setCreateTeamOpen] = useState(false);
  const [createTeamLoading, setCreateTeamLoading] = useState(false);
  const [teamForm, setTeamForm] = useState({ teamName: "", description: "", size: "" });
  const [teamFormErrors, setTeamFormErrors] = useState({});

  // Add Member
  const [memberOpen, setMemberOpen] = useState(false);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberTeamId, setMemberTeamId] = useState("");
  const [memberForm, setMemberForm] = useState({
    name: "",
    email: "",
    designation: "",
    role: "MEMBER",
    joinDate: "",
  });
  const [memberErrors, setMemberErrors] = useState({});

  // Audit trail
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditEvents, setAuditEvents] = useState([]);

  // Templates
  const [templates, setTemplates] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);

  const resetFlash = () => {
    setError("");
    setSuccess("");
  };

  const loadEmployments = async () => {
    const res = await sectionsAPI.employment.getAll();
    const list = Array.isArray(res.data) ? res.data : [];
    if (!list.length) throw new Error("No employment found. Please add employment first.");
    setEmployments(list);
    const firstId = list[0]?.id || "";
    setSelectedEmploymentId(firstId);
    return firstId;
  };

  const loadReferences = async (employmentId) => {
    const res = await sectionsAPI.reference.getAll(employmentId);
    setReferences(Array.isArray(res.data) ? res.data : []);
  };

  const loadTeams = async (employmentId) => {
    setTeamLoading(true);
    try {
      const res = await sectionsAPI.team.getAll(employmentId);
      setTeams(Array.isArray(res.data) ? res.data : []);
    } finally {
      setTeamLoading(false);
    }
  };

  const loadTemplates = async () => {
    setTemplateLoading(true);
    try {
      const res = await sectionsAPI.questionnaire.getDefault();
      const data = res.data;
      const list = Array.isArray(data) ? data : data ? [data] : [];
      setTemplates(list);
    } finally {
      setTemplateLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        resetFlash();
        const empId = await loadEmployments();
        await Promise.all([loadReferences(empId), loadTeams(empId), loadTemplates()]);
      } catch (e) {
        setError(e?.message || formatAPIError(e));
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredReferences = useMemo(() => {
    const q = refSearch.trim().toLowerCase();
    if (!q) return references;
    return references.filter((r) => {
      const name = (r?.name || "").toLowerCase();
      const email = (r?.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [references, refSearch]);

  const validateSend = () => {
    const errs = {};
    const n = validateName(sendForm.name);
    if (n) errs.name = n;
    const em = validateEmail(sendForm.email);
    if (em) errs.email = em;
    return errs;
  };

  const handleEmploymentChange = async (employmentId) => {
    resetFlash();
    setSelectedEmploymentId(employmentId);
    await Promise.all([loadReferences(employmentId), loadTeams(employmentId)]);
  };

  const handleSendReference = async () => {
    resetFlash();
    const errs = validateSend();
    setSendErrors(errs);
    if (Object.keys(errs).length) return;

    if (!selectedEmploymentId) {
      setError("Employment not found. Please add employment first.");
      return;
    }

    try {
      setSendLoading(true);
      const payload = {
        name: sendForm.name.trim(),
        email: sendForm.email.trim(),
        designation: sendForm.designation?.trim() || undefined,
        contactNumber: sendForm.contactNumber?.trim() || undefined,
        teamId: sendForm.teamId || undefined,
        questionnaireTemplateId: sendForm.questionnaireTemplateId || undefined,
      };
      await sectionsAPI.reference.add(selectedEmploymentId, payload);

      setSuccess("Reference request sent successfully.");
      setSendOpen(false);
      setSendForm({
        name: "",
        email: "",
        designation: "",
        contactNumber: "",
        teamId: "",
        questionnaireTemplateId: "",
      });

      await loadReferences(selectedEmploymentId);
    } catch (e) {
      setError(formatAPIError(e));
    } finally {
      setSendLoading(false);
    }
  };

  const handleResend = async (referenceId) => {
    resetFlash();
    try {
      await sectionsAPI.reference.resend(referenceId);
      setSuccess("Reminder sent successfully.");
      if (selectedEmploymentId) await loadReferences(selectedEmploymentId);
    } catch (e) {
      setError(formatAPIError(e));
    }
  };

  const loadAuditTrail = async (refId) => {
    resetFlash();
    try {
      const res = await sectionsAPI.reference.getAuditTrail(refId);
      const items = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setAuditEvents(items);
      setAuditOpen(true);
    } catch (e) {
      setError(formatAPIError(e));
    }
  };

  const validateCreateTeam = () => {
    const errs = {};
    if (!teamForm.teamName?.trim()) errs.teamName = "Team name is required";
    return errs;
  };

  const handleCreateTeam = async () => {
    resetFlash();
    const errs = validateCreateTeam();
    setTeamFormErrors(errs);
    if (Object.keys(errs).length) return;

    if (!selectedEmploymentId) {
      setError("Employment not selected.");
      return;
    }

    try {
      setCreateTeamLoading(true);
      await sectionsAPI.team.create(selectedEmploymentId, {
        teamName: teamForm.teamName.trim(),
        description: teamForm.description?.trim() || undefined,
        size: teamForm.size ? Number(teamForm.size) : undefined,
      });
      setSuccess("Team created successfully.");
      setCreateTeamOpen(false);
      setTeamForm({ teamName: "", description: "", size: "" });
      await loadTeams(selectedEmploymentId);
    } catch (e) {
      setError(formatAPIError(e));
    } finally {
      setCreateTeamLoading(false);
    }
  };

  const validateMember = () => {
    const errs = {};
    const n = validateName(memberForm.name);
    if (n) errs.name = n;
    const em = validateEmail(memberForm.email);
    if (em) errs.email = em;
    return errs;
  };

  const handleAddMember = async () => {
    resetFlash();
    const errs = validateMember();
    setMemberErrors(errs);
    if (Object.keys(errs).length) return;

    if (!selectedEmploymentId) {
      setError("Employment not selected.");
      return;
    }
    if (!memberTeamId) {
      setError("Select a team first.");
      return;
    }

    try {
      setMemberLoading(true);
      await sectionsAPI.team.addMember(selectedEmploymentId, memberTeamId, {
        name: memberForm.name.trim(),
        email: memberForm.email.trim(),
        designation: memberForm.designation?.trim() || undefined,
        role: memberForm.role || undefined,
        joinDate: memberForm.joinDate || undefined,
      });

      setSuccess("Member added successfully.");
      setMemberOpen(false);
      setMemberForm({ name: "", email: "", designation: "", role: "MEMBER", joinDate: "" });
      setMemberTeamId("");
      await loadTeams(selectedEmploymentId);
    } catch (e) {
      setError(formatAPIError(e));
    } finally {
      setMemberLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="cr-page">
          <div className="cr-container">
            <div className="cr-loading">Loading references...</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="cr-page">
        <div className="cr-container">
          <div className="cr-top">
            <div className="cr-top-left">
              <h2 className="cr-title">Reference Management</h2>
              <p className="cr-subtitle">Manage your professional references and reference requests</p>
            </div>

            <div className="cr-top-actions">
              <button className="cr-btn cr-btn-secondary" onClick={() => navigate(-1)}>
                Back
              </button>
              <button className="cr-btn cr-btn-primary" onClick={() => setSendOpen(true)}>
                Send Request
              </button>
            </div>
          </div>

          {/* NEW: Employment selector (still using same .cr-input style) */}
          <div className="cr-card" style={{ marginTop: 12 }}>
            <div className="cr-search-row" style={{ justifyContent: "space-between" }}>
              <div style={{ fontWeight: 900 }}>Selected Employment</div>
              <select
                className="cr-input"
                value={selectedEmploymentId}
                onChange={(e) => handleEmploymentChange(e.target.value)}
                style={{ maxWidth: 420 }}
              >
                {employments.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.company || emp.companyName || emp.jobTitle || emp.title || emp.id}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="cr-flash">
            {error && <div className="cr-error">{error}</div>}
            {success && <div className="cr-success">{success}</div>}
          </div>

          <div className="cr-tabs">
            {TABS.map((t) => (
              <button
                key={t}
                className={`cr-tab ${activeTab === t ? "active" : ""}`}
                onClick={() => setActiveTab(t)}
              >
                {t}
              </button>
            ))}
          </div>

          {activeTab === "Requests" ? (
            <div className="cr-card">
              <div className="cr-search-row">
                <input
                  className="cr-input"
                  value={refSearch}
                  onChange={(e) => setRefSearch(e.target.value)}
                  placeholder="Search by name or email..."
                />
                <button
                  className="cr-btn cr-btn-primary"
                  onClick={() => selectedEmploymentId && loadReferences(selectedEmploymentId)}
                >
                  Refresh
                </button>
              </div>

              <div className="cr-body">
                {!filteredReferences.length ? (
                  <div className="cr-empty">Search for colleagues to request references</div>
                ) : (
                  <div className="cr-list">
                    {filteredReferences.map((r) => {
                      const canRemind = (r?.status || "").toUpperCase() === "PENDING_ACK";
                      return (
                        <div className="cr-row" key={r.id}>
                          <div className="cr-row-left">
                            <div className="cr-row-title">{r.name}</div>
                            <div className="cr-row-sub">{r.email}</div>
                            <div className="cr-row-sub">Status: {r.status || "PENDING_ACK"}</div>
                            <div className="cr-row-sub">
                              Sent: {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "-"}
                            </div>
                            {r.questionnaireTitle && (
                              <div className="cr-row-sub">{r.questionnaireTitle}</div>
                            )}
                          </div>

                          <div className="cr-row-actions">
                            {canRemind && (
                              <button className="cr-btn cr-btn-outline" onClick={() => handleResend(r.id)}>
                                Remind
                              </button>
                            )}
                            <button className="cr-btn cr-btn-outline" onClick={() => loadAuditTrail(r.id)}>
                              Audit Trail
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    <div className="cr-confidential">
                      Reference content is confidential and not visible to you. You can only see the status of requests.
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="cr-card">
              <div className="cr-card-head">
                <div className="cr-card-title">Teams</div>
                <div className="cr-card-actions">
                  <button
                    className="cr-btn cr-btn-outline"
                    onClick={() => selectedEmploymentId && loadTeams(selectedEmploymentId)}
                    disabled={teamLoading}
                  >
                    Refresh
                  </button>
                  <button className="cr-btn cr-btn-primary" onClick={() => setCreateTeamOpen(true)}>
                    Create Team
                  </button>
                </div>
              </div>

              <div className="cr-body">
                {!teams.length ? (
                  <div className="cr-empty">No teams created yet.</div>
                ) : (
                  <div className="cr-list">
                    {teams.map((t) => (
                      <div className="cr-team" key={t.id}>
                        <div className="cr-team-head">
                          <div>
                            <div className="cr-row-title">{t.teamName}</div>
                            <div className="cr-row-sub">
                              Members: {Array.isArray(t.members) ? t.members.length : 0}
                            </div>
                          </div>

                          <button
                            className="cr-btn cr-btn-outline"
                            onClick={() => {
                              setMemberTeamId(t.id);
                              setMemberOpen(true);
                            }}
                          >
                            Add Member
                          </button>
                        </div>

                        {Array.isArray(t.members) && t.members.length > 0 && (
                          <div className="cr-team-members">
                            {t.members.map((m) => (
                              <div className="cr-member" key={m.id}>
                                <div>
                                  <div className="cr-member-name">{m.name}</div>
                                  <div className="cr-member-email">{m.email}</div>
                                  {m.designation && <div className="cr-member-meta">{m.designation}</div>}
                                  {m.joinDate && (
                                    <div className="cr-member-meta">
                                      Joined: {new Date(m.joinDate).toLocaleDateString()}
                                    </div>
                                  )}
                                </div>
                                <div className="cr-member-role">{m.role}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* SEND MODAL */}
          {sendOpen && (
            <div className="cr-modal-overlay">
              <div className="cr-modal">
                <div className="cr-modal-head">
                  <div className="cr-modal-title">Send Reference Request</div>
                  <button className="cr-modal-close" onClick={() => setSendOpen(false)}>
                    ×
                  </button>
                </div>

                <div className="cr-modal-body">
                  <div className="cr-grid-2">
                    <div className="cr-field">
                      <label className="cr-label">Name</label>
                      <input
                        className={`cr-input ${sendErrors.name ? "error" : ""}`}
                        value={sendForm.name}
                        onChange={(e) => setSendForm((p) => ({ ...p, name: e.target.value }))}
                        placeholder="Reference name"
                      />
                      {sendErrors.name && <div className="cr-field-error">{sendErrors.name}</div>}
                    </div>

                    <div className="cr-field">
                      <label className="cr-label">Email</label>
                      <input
                        className={`cr-input ${sendErrors.email ? "error" : ""}`}
                        value={sendForm.email}
                        onChange={(e) => setSendForm((p) => ({ ...p, email: e.target.value }))}
                        placeholder="name@company.com"
                      />
                      {sendErrors.email && <div className="cr-field-error">{sendErrors.email}</div>}
                    </div>
                  </div>

                  <div className="cr-grid-2">
                    <div className="cr-field">
                      <label className="cr-label">Designation (optional)</label>
                      <input
                        className="cr-input"
                        value={sendForm.designation}
                        onChange={(e) => setSendForm((p) => ({ ...p, designation: e.target.value }))}
                        placeholder="Manager / Lead / HR..."
                      />
                    </div>

                    <div className="cr-field">
                      <label className="cr-label">Contact Number (optional)</label>
                      <input
                        className="cr-input"
                        value={sendForm.contactNumber}
                        onChange={(e) => setSendForm((p) => ({ ...p, contactNumber: e.target.value }))}
                        placeholder="+91..."
                      />
                    </div>
                  </div>

                  <div className="cr-grid-2">
                    <div className="cr-field">
                      <label className="cr-label">Team (optional)</label>
                      <select
                        className="cr-input"
                        value={sendForm.teamId}
                        onChange={(e) => setSendForm((p) => ({ ...p, teamId: e.target.value }))}
                      >
                        <option value="">Select team</option>
                        {teams.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.teamName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="cr-field">
                      <label className="cr-label">Questionnaire (optional)</label>
                      <select
                        className="cr-input"
                        value={sendForm.questionnaireTemplateId}
                        onChange={(e) =>
                          setSendForm((p) => ({ ...p, questionnaireTemplateId: e.target.value }))
                        }
                        disabled={templateLoading}
                      >
                        <option value="">Default / None</option>
                        {templates.map((tpl) => (
                          <option key={tpl.id} value={tpl.id}>
                            {tpl.title || tpl.name || tpl.id}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="cr-modal-foot">
                  <button className="cr-btn cr-btn-secondary" onClick={() => setSendOpen(false)}>
                    Cancel
                  </button>
                  <button className="cr-btn cr-btn-primary" onClick={handleSendReference} disabled={sendLoading}>
                    {sendLoading ? "Sending..." : "Send"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* CREATE TEAM MODAL */}
          {createTeamOpen && (
            <div className="cr-modal-overlay">
              <div className="cr-modal">
                <div className="cr-modal-head">
                  <div className="cr-modal-title">Create Team</div>
                  <button className="cr-modal-close" onClick={() => setCreateTeamOpen(false)}>
                    ×
                  </button>
                </div>

                <div className="cr-modal-body">
                  <div className="cr-field">
                    <label className="cr-label">Team Name</label>
                    <input
                      className={`cr-input ${teamFormErrors.teamName ? "error" : ""}`}
                      value={teamForm.teamName}
                      onChange={(e) => setTeamForm((p) => ({ ...p, teamName: e.target.value }))}
                      placeholder="e.g., Engineering"
                    />
                    {teamFormErrors.teamName && <div className="cr-field-error">{teamFormErrors.teamName}</div>}
                  </div>

                  <div className="cr-field">
                    <label className="cr-label">Description (optional)</label>
                    <input
                      className="cr-input"
                      value={teamForm.description}
                      onChange={(e) => setTeamForm((p) => ({ ...p, description: e.target.value }))}
                      placeholder="Short description"
                    />
                  </div>

                  <div className="cr-field">
                    <label className="cr-label">Size (optional)</label>
                    <input
                      className="cr-input"
                      value={teamForm.size}
                      onChange={(e) => setTeamForm((p) => ({ ...p, size: e.target.value }))}
                      placeholder="e.g., 8"
                    />
                  </div>
                </div>

                <div className="cr-modal-foot">
                  <button className="cr-btn cr-btn-secondary" onClick={() => setCreateTeamOpen(false)}>
                    Cancel
                  </button>
                  <button className="cr-btn cr-btn-primary" onClick={handleCreateTeam} disabled={createTeamLoading}>
                    {createTeamLoading ? "Creating..." : "Create"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ADD MEMBER MODAL */}
          {memberOpen && (
            <div className="cr-modal-overlay">
              <div className="cr-modal">
                <div className="cr-modal-head">
                  <div className="cr-modal-title">Add Team Member</div>
                  <button className="cr-modal-close" onClick={() => setMemberOpen(false)}>
                    ×
                  </button>
                </div>

                <div className="cr-modal-body">
                  <div className="cr-grid-2">
                    <div className="cr-field">
                      <label className="cr-label">Name</label>
                      <input
                        className={`cr-input ${memberErrors.name ? "error" : ""}`}
                        value={memberForm.name}
                        onChange={(e) => setMemberForm((p) => ({ ...p, name: e.target.value }))}
                        placeholder="Member name"
                      />
                      {memberErrors.name && <div className="cr-field-error">{memberErrors.name}</div>}
                    </div>

                    <div className="cr-field">
                      <label className="cr-label">Email</label>
                      <input
                        className={`cr-input ${memberErrors.email ? "error" : ""}`}
                        value={memberForm.email}
                        onChange={(e) => setMemberForm((p) => ({ ...p, email: e.target.value }))}
                        placeholder="member@company.com"
                      />
                      {memberErrors.email && <div className="cr-field-error">{memberErrors.email}</div>}
                    </div>
                  </div>

                  <div className="cr-grid-2">
                    <div className="cr-field">
                      <label className="cr-label">Designation (optional)</label>
                      <input
                        className="cr-input"
                        value={memberForm.designation}
                        onChange={(e) => setMemberForm((p) => ({ ...p, designation: e.target.value }))}
                        placeholder="Senior Developer"
                      />
                    </div>

                    <div className="cr-field">
                      <label className="cr-label">Join Date (optional)</label>
                      <input
                        type="date"
                        className="cr-input"
                        value={memberForm.joinDate}
                        onChange={(e) => setMemberForm((p) => ({ ...p, joinDate: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="cr-field">
                    <label className="cr-label">Role (optional)</label>
                    <select
                      className="cr-input"
                      value={memberForm.role}
                      onChange={(e) => setMemberForm((p) => ({ ...p, role: e.target.value }))}
                    >
                      <option value="MEMBER">Member</option>
                      <option value="MANAGER">Manager</option>
                      <option value="PEER">Peer</option>
                      <option value="SUBORDINATE">Subordinate</option>
                    </select>
                  </div>
                </div>

                <div className="cr-modal-foot">
                  <button className="cr-btn cr-btn-secondary" onClick={() => setMemberOpen(false)}>
                    Cancel
                  </button>
                  <button className="cr-btn cr-btn-primary" onClick={handleAddMember} disabled={memberLoading}>
                    {memberLoading ? "Adding..." : "Add"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* AUDIT MODAL */}
          {auditOpen && (
            <div className="cr-modal-overlay">
              <div className="cr-modal">
                <div className="cr-modal-head">
                  <div className="cr-modal-title">Audit Trail ({auditEvents.length} events)</div>
                  <button className="cr-modal-close" onClick={() => setAuditOpen(false)}>
                    ×
                  </button>
                </div>
                <div className="cr-modal-body">
                  {!auditEvents.length ? (
                    <div className="cr-empty">No audit events found.</div>
                  ) : (
                    auditEvents.map((event, idx) => (
                      <div key={idx} className="cr-audit-event">
                        <div className="cr-audit-action">{event.action}</div>
                        <div className="cr-audit-time">
                          {new Date(event.createdAt || event.timestamp).toLocaleString()}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="cr-modal-foot">
                  <button className="cr-btn cr-btn-secondary" onClick={() => setAuditOpen(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CandidateReferences;
